<template>
  <div v-if="dialog" class="api-usage-page">
    <SnackbarComponent
      v-model="snackbar.show"
      :message="snackbar.message"
      :color="snackbar.color"
    />

    <div class="page-actions">
      <div>
        <h1 class="page-title">
          API Usage – {{ organization?.organization_name || "-" }}
        </h1>
        <p class="page-subtitle">
          Monitor and analyze API usage{{
            showApiCallCost ? ", errors and costs" : " and errors"
          }}
          across all endpoints.
        </p>
      </div>

      <div class="header-actions">
        <v-btn
          variant="outlined"
          color="#334155"
          class="action-btn"
          prepend-icon="mdi-arrow-left"
          @click="dialog = false"
        >
          Back
        </v-btn>
        <v-btn
          variant="outlined"
          color="error"
          class="action-btn"
          prepend-icon="mdi-restore"
          :loading="resetting"
          :disabled="loading || !organization?.organization_id"
          @click="confirmReset = true"
        >
          Reset
        </v-btn>
        <v-btn
          color="primary"
          class="action-btn"
          prepend-icon="mdi-refresh"
          :loading="loading"
          :disabled="resetting"
          @click="fetchApiUsage"
        >
          Refresh
        </v-btn>
      </div>
    </div>

    <v-dialog v-model="confirmReset" max-width="420" persistent>
      <v-card rounded="lg">
        <v-card-title class="text-h6">Reset API Usage?</v-card-title>
        <v-card-text>
          This will reset all API usage counters for
          <strong>{{ organization?.organization_name || "this organization" }}</strong>.
          This action cannot be undone.
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer />
          <v-btn
            variant="text"
            :disabled="resetting"
            @click="confirmReset = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            variant="flat"
            :loading="resetting"
            @click="resetApiUsage"
          >
            Reset
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      height="3"
      class="load-bar"
    />

    <!-- Summary card -->
    <div class="summary-card">
      <div
        class="summary-grid"
        :class="{ 'summary-grid--no-cost': !showApiCallCost }"
      >
        <div class="summary-item">
          <div class="summary-label">API Access</div>
          <div
            class="summary-value"
            :class="apiEnabled ? 'is-success' : 'is-error'"
          >
            {{ apiEnabled ? "Enabled" : "Disabled" }}
          </div>
          <div class="summary-foot">
            {{
              apiEnabled
                ? "API access is currently enabled"
                : "API access is currently disabled"
            }}
          </div>
        </div>

        <div class="summary-item">
          <div class="summary-label">Total Requests</div>
          <div class="summary-value">{{ totalRequests }}</div>
          <div class="summary-foot">Across all {{ endpointCards.length }} API endpoints</div>
        </div>

        <div class="summary-item">
          <div class="summary-label">Total Errors</div>
          <div class="summary-value">{{ totalErrors }}</div>
          <div class="summary-foot">
            Tracked, not counted toward request limit
          </div>
        </div>

        <div v-if="showApiCallCost" class="summary-item">
          <div class="summary-label">Total Cost</div>
          <div class="summary-value">${{ totalCost }}</div>
          <div class="summary-foot">Cost incurred across all APIs</div>
        </div>
      </div>
    </div>

    <div class="section-head">
      <h2 class="section-title">Endpoint Metrics</h2>
      <p class="section-subtitle">
        Usage{{ showApiCallCost ? ", errors and cost" : " and errors" }}
        breakdown by endpoint.
      </p>
    </div>

    <div class="endpoint-grid">
      <div
        v-for="api in endpointCards"
        :key="api.key"
        class="endpoint-card"
      >
        <div class="endpoint-top">
          <div class="endpoint-name">{{ api.title }}</div>
          <div class="endpoint-usage">
            <div class="usage-ratio">
              {{ api.requests }} / {{ api.limitLabel }}
            </div>
            <div class="usage-percent">{{ api.percentUsed }}%</div>
          </div>
        </div>

        <div class="progress-track">
          <div
            class="progress-fill"
            :style="{
              width: `${api.percentUsed}%`,
              backgroundColor: progressColor(api.percentUsed),
            }"
          ></div>
        </div>

        <div
          class="endpoint-stats"
          :class="{ 'endpoint-stats--no-cost': !showApiCallCost }"
        >
          <div class="stat-col">
            <div class="stat-label">Errors</div>
            <div class="stat-value">{{ api.errors }}</div>
          </div>
          <template v-if="showApiCallCost">
            <div class="stat-divider"></div>
            <div class="stat-col">
              <div class="stat-label">Cost / call</div>
              <div class="stat-value">${{ api.costPerCall }}</div>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-col">
              <div class="stat-label">Cost incurred</div>
              <div class="stat-value">${{ api.costIncurred }}</div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { generateClient } from "aws-amplify/api";
import { get_organization_api_usage } from "@/graphql/queries";
import { reset_organization_api_usage } from "@/graphql/mutations";
import SnackbarComponent from "@/components/Extras/Snackbar.vue";

const client = generateClient();

const ENDPOINT_META = {
  list_resident_medications: {
    title: "List Resident Medications",
  },
  list_facility_residents: {
    title: "List Facility Residents",
  },
  list_reports: {
    title: "List Reports",
  },
  list_facilities: {
    title: "List Facilities",
  },
  list_resident_recommendations: {
    title: "List Resident Recommendations",
  },
  list_all_recommendations: {
    title: "List All Recommendations",
  },
};

const ENDPOINT_ORDER = [
  "list_resident_medications",
  "list_facility_residents",
  "list_reports",
  "list_facilities",
  "list_resident_recommendations",
  "list_all_recommendations",
];

export default {
  name: "OrganizationApiUsageDialog",
  components: {
    SnackbarComponent,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    organization: {
      type: Object,
      default: null,
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      loading: false,
      resetting: false,
      confirmReset: false,
      usageData: null,
      snackbar: {
        show: false,
        message: "",
        color: "error",
      },
    };
  },
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    showApiCallCost() {
      const value = this.organization?.show_api_call_cost;
      return value === true || String(value).trim().toLowerCase() === "true";
    },
    apiEnabled() {
      return this.usageData?.api_enabled === true;
    },
    endpointCards() {
      const metrics = this.usageData?.endpoint_metrics || {};
      const keys = ENDPOINT_ORDER.filter((key) => metrics[key] !== undefined);
      const remaining = Object.keys(metrics).filter(
        (key) => !keys.includes(key)
      );
      const orderedKeys = [...keys, ...remaining];

      return orderedKeys.map((key) => {
        const item = metrics[key] || {};
        const meta = ENDPOINT_META[key] || {
          title: key.replace(/_/g, " "),
        };
        const requests = Number(item.requests || 0);
        const errors = Number(item.errors || 0);
        const limit = Number(item.limit || 0);
        let percentUsed = 0;

        if (item.percent_used !== undefined && item.percent_used !== null) {
          percentUsed = Number(item.percent_used);
        } else if (limit > 0) {
          percentUsed = (requests / limit) * 100;
        }

        if (Number.isNaN(percentUsed)) percentUsed = 0;
        percentUsed = Math.min(Math.round(percentUsed), 100);

        return {
          key,
          title: meta.title,
          requests,
          errors,
          limit,
          limitLabel: limit > 0 ? limit : "-",
          percentUsed,
          costPerCall: Number(item.cost_per_call || 0).toFixed(2),
          costIncurred: Number(item.cost_incurred || 0).toFixed(2),
        };
      });
    },
    totalRequests() {
      return this.endpointCards.reduce((sum, api) => sum + api.requests, 0);
    },
    totalErrors() {
      return this.endpointCards.reduce((sum, api) => sum + api.errors, 0);
    },
    totalCost() {
      const total = this.endpointCards.reduce(
        (sum, api) => sum + Number(api.costIncurred || 0),
        0
      );
      return total.toFixed(2);
    },
  },
  watch: {
    dialog: {
      immediate: true,
      handler(val) {
        if (val) {
          this.fetchApiUsage();
        } else {
          this.usageData = null;
          this.confirmReset = false;
          this.resetting = false;
          this.snackbar.show = false;
        }
      },
    },
  },
  methods: {
    progressColor(percent) {
      const p = Math.max(0, Math.min(100, Number(percent) || 0));

      if (p >= 100) return "#DC2626";
      if (p <= 0) return "#CBD5E1";

      // 0 – 10: light grey → light blue
      if (p < 10) {
        return this.mixHex("#CBD5E1", "#93C5FD", p / 10);
      }

      // 10 – 50: bright blue → darker blue
      if (p < 50) {
        return this.mixHex("#60A5FA", "#1D4ED8", (p - 10) / 40);
      }

      // 50 – 99: bright orange → near-red orange
      return this.mixHex("#FB923C", "#EF4444", (p - 50) / 49);
    },
    mixHex(fromHex, toHex, t) {
      const clamp = Math.max(0, Math.min(1, t));
      const from = this.hexToRgb(fromHex);
      const to = this.hexToRgb(toHex);
      const r = Math.round(from.r + (to.r - from.r) * clamp);
      const g = Math.round(from.g + (to.g - from.g) * clamp);
      const b = Math.round(from.b + (to.b - from.b) * clamp);
      return `rgb(${r}, ${g}, ${b})`;
    },
    hexToRgb(hex) {
      const cleaned = hex.replace("#", "");
      return {
        r: parseInt(cleaned.slice(0, 2), 16),
        g: parseInt(cleaned.slice(2, 4), 16),
        b: parseInt(cleaned.slice(4, 6), 16),
      };
    },
    showError(message) {
      this.snackbar = {
        show: true,
        message: message || "An error occurred while loading API usage",
        color: "error",
      };
    },
    async resetApiUsage() {
      if (!this.organization?.organization_id || this.resetting) return;

      this.resetting = true;
      try {
        const res = await client.graphql({
          query: reset_organization_api_usage,
          variables: {
            input: {
              organization_id: this.organization.organization_id,
            },
          },
        });

        let parsed = res.data.reset_organization_api_usage;
        if (typeof parsed === "string") {
          parsed = JSON.parse(parsed);
        }

        if (parsed?.status && parsed.status !== "SUCCESS") {
          throw new Error(
            parsed.status_message || "Failed to reset API usage"
          );
        }

        this.confirmReset = false;
        this.snackbar = {
          show: true,
          message: parsed?.status_message || "API usage reset successfully",
          color: "success",
        };
        await this.fetchApiUsage();
      } catch (error) {
        console.error("Error resetting API usage:", error);
        this.showError(
          error?.errors?.[0]?.message ||
            error?.message ||
            "An error occurred while resetting API usage"
        );
      } finally {
        this.resetting = false;
      }
    },
    async fetchApiUsage() {
      if (!this.organization?.organization_id) return;

      this.loading = true;
      this.snackbar.show = false;
      try {
        const res = await client.graphql({
          query: get_organization_api_usage,
          variables: {
            input: {
              organization_id: this.organization.organization_id,
            },
          },
        });

        let parsed = res.data.get_organization_api_usage;
        if (typeof parsed === "string") {
          parsed = JSON.parse(parsed);
        }
        parsed = parsed || {};

        if (parsed.status && parsed.status !== "SUCCESS") {
          this.usageData = null;
          this.showError(parsed.status_message || "Failed to load API usage");
          return;
        }

        this.usageData = parsed.data || parsed;

        const metrics = this.usageData?.endpoint_metrics || {};
        if (!Object.keys(metrics).length) {
          this.snackbar = {
            show: true,
            message: "No endpoint metrics found for this organization.",
            color: "info",
          };
        }
      } catch (error) {
        console.error("Error fetching API usage:", error);
        this.usageData = null;
        this.showError(
          error?.errors?.[0]?.message ||
            error?.message ||
            "An error occurred while loading API usage"
        );
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.api-usage-page {
  width: 100%;
  min-height: calc(100vh - 32px);
  box-sizing: border-box;
  padding: 4px 4px 20px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.page-actions {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.page-title {
  margin: 0;
  font-size: clamp(18px, 1.6vw, 22px);
  line-height: 1.2;
  font-weight: 700;
  color: #0f172a;
}

.page-subtitle {
  margin: 4px 0 0;
  font-size: 13px;
  color: #64748b;
}

.header-actions {
  display: flex;
  gap: 10px;
  flex-shrink: 0;
}

.action-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
}

.load-bar {
  flex-shrink: 0;
}

.summary-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
  padding: 20px 8px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.summary-grid--no-cost {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.summary-item {
  padding: 4px 20px;
  min-width: 0;
  border-right: 1px solid #e2e8f0;
}

.summary-item:last-child {
  border-right: none;
}

.summary-label {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 4px;
}

.summary-value {
  font-size: clamp(22px, 2vw, 28px);
  font-weight: 700;
  color: #0f172a;
  line-height: 1.15;
}

.summary-value.is-success {
  color: #16a34a;
}

.summary-value.is-error {
  color: #dc2626;
}

.summary-foot {
  margin-top: 6px;
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.35;
}

.section-head {
  margin-top: 4px;
}

.section-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.section-subtitle {
  margin: 2px 0 0;
  font-size: 12px;
  color: #64748b;
}

.endpoint-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.endpoint-card {
  background: #ffffff;
  border-radius: 14px;
  padding: 16px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}

.endpoint-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.endpoint-name {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.3;
  min-width: 0;
}

.endpoint-usage {
  text-align: right;
  flex-shrink: 0;
}

.usage-ratio {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
}

.usage-percent {
  margin-top: 2px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.progress-track {
  height: 8px;
  border-radius: 999px;
  background: #e2e8f0;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.25s ease;
}

.endpoint-stats {
  display: grid;
  grid-template-columns: 1fr auto 1fr auto 1fr;
  align-items: start;
  gap: 0;
  padding-top: 4px;
}

.endpoint-stats--no-cost {
  grid-template-columns: 1fr;
}

.endpoint-stats--no-cost .stat-col {
  padding: 0;
}

.stat-col {
  min-width: 0;
  padding: 0 8px;
}

.stat-col:first-child {
  padding-left: 0;
}

.stat-col:last-child {
  padding-right: 0;
}

.stat-divider {
  width: 1px;
  background: #e2e8f0;
  align-self: stretch;
  min-height: 36px;
}

.stat-label {
  font-size: 11px;
  color: #94a3b8;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

@media (max-width: 1280px) {
  .summary-grid,
  .summary-grid--no-cost {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .summary-item:nth-child(2) {
    border-right: none;
  }

  .summary-item:nth-child(1),
  .summary-item:nth-child(2) {
    border-bottom: 1px solid #e2e8f0;
    padding-bottom: 16px;
    margin-bottom: 12px;
  }

  .summary-grid--no-cost .summary-item:nth-child(3) {
    border-right: none;
    grid-column: 1 / -1;
  }

  .endpoint-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .summary-grid {
    grid-template-columns: 1fr;
  }

  .summary-item {
    border-right: none;
    border-bottom: 1px solid #e2e8f0;
    padding: 12px 12px;
    margin-bottom: 0;
  }

  .summary-item:nth-child(1),
  .summary-item:nth-child(2) {
    border-bottom: 1px solid #e2e8f0;
    margin-bottom: 0;
  }

  .summary-item:last-child {
    border-bottom: none;
  }

  .endpoint-grid {
    grid-template-columns: 1fr;
  }

  .page-actions {
    flex-direction: column;
  }

  .header-actions {
    width: 100%;
  }

  .header-actions .action-btn {
    flex: 1;
  }
}
</style>
